import type { IUiThreeDPresentation } from '@/engine/UIPresentation/Interfaces';
import type { ICameraInitializer, IViewPortController } from '../../interfaces';
import type { ViewsService } from '../../Views.service';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type * as THREE from 'three';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';

export class ImagesCameraInitializer implements ICameraInitializer {
  private _viewportController: IViewPortController;

  private _viewsService: ViewsService;

  private _planningAlgorithm: PlanningAlgorithm;

  private _imagePresentation?: IUiThreeDPresentation;

  private _areImagesReady = false;

  private _initializationJob?: () => void;

  public constructor(
    viewportController: IViewPortController,
    viewsService: ViewsService,
    planningAlgorithm: PlanningAlgorithm
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._viewportController = viewportController;
  }

  public setImagePresentation(presentation: IUiThreeDPresentation): void {
    this._imagePresentation = presentation;
  }

  public setAreImagesReady(areImagesReady: boolean): void {
    this._areImagesReady = areImagesReady;
  }

  public fitCameraToBox(
    minCorner: THREE.Vector3,
    maxCorner: THREE.Vector3,
    _orientationTop: THREE.Vector3,
    _orientationDir: THREE.Vector3,
    fittingFactor: number
  ): void {
    // do not set camera orientation for an images ViewPort as it is being managed separately - it must be always in sync with image plane
    const job = (): void => {
      this._viewportController.getCameraController().fitCameraToPoints([minCorner, maxCorner], { fittingFactor });
    };
    if (!this._areImagesReady || !this._imagePresentation) {
      this._initializationJob = job;
    } else {
      job();
    }
  }

  public release(): void {
    if (this._imagePresentation && this._areImagesReady && this._initializationJob) {
      this._initializationJob();
      this._initializationJob = undefined;
    }
  }

  public initialize(): void {
    if (!this._areImagesReady || !this._imagePresentation) {
      this._initializationJob = this._initJob.bind(this);
    } else {
      this._initializationJob = undefined;
      this._initJob();
    }
  }

  private _initJob(): void {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      return;
    }

    const imagesRoiConfig = currentView.getImagesRoiConfig();
    if (imagesRoiConfig) {
      const roi = this._planningAlgorithm.getValueStrict(imagesRoiConfig.descriptor, DT.RegionOfInterest).value;
      this.fitCameraToBox(roi.minCorner, roi.maxCorner, roi.dirZ, roi.dirY, imagesRoiConfig.zoomFactor);
    } else {
      if (!this._imagePresentation) {
        throw new Error('Main images presentation expected to be existent so far');
      }
      this._viewportController.fitCameraToObjects(1.0, [this._imagePresentation.get3dObject() as THREE.Mesh]);
    }
  }

  public dispose(): void {
    // noop
  }
}
